// Product Master Excel export / import (data_export_import_to_excel_fragment.xml
// + ExcelParser.java). The Android sheet is named "Product Details"; we round-trip
// the same shape plus a web-added "Product Sub Category" column. Export produces
// CSV (opens in Excel) or a real Excel 2003 SpreadsheetML .xls (no external
// dependency, unlike Android's Apache POI). Import parses either CSV or that
// SpreadsheetML back and is header-driven, so legacy 8-column files (without the
// sub-category column) still import correctly.

import { Product } from '../types';

// Header labels. The first eight mirror the Android ExcelParser.WriteExcel order;
// "Product Sub Category" is inserted after "Product Category" (web addition).
export const PRODUCT_EXCEL_HEADERS = [
  'Product Name',
  'Regional Name',
  'Product Price',
  'Product Category',
  'Product Sub Category',
  'Stockable',
  'Weightable',
  'Stock',
  'Tax',
] as const;

const yn = (b: boolean | undefined) => (b ? 'YES' : 'NO');

export function productToRow(p: Product): string[] {
  return [
    p.name ?? '',
    p.regionalName ?? '',
    String(p.price ?? 0),
    p.category ?? '',
    p.subCategory ?? '',
    yn(p.stockable),
    yn(p.weighable),
    String(p.stock ?? 0),
    p.taxRatePercent != null ? String(p.taxRatePercent) : '',
  ];
}

// ---- Export ----

const csvCell = (s: string) => `"${s.replace(/"/g, '""')}"`;

export function buildProductCsv(products: Product[]): string {
  const lines = [PRODUCT_EXCEL_HEADERS.join(',')];
  products.forEach((p) => lines.push(productToRow(p).map(csvCell).join(',')));
  return lines.join('\r\n');
}

const xmlEsc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Excel 2003 SpreadsheetML — a single XML file Excel opens as a real workbook.
export function buildProductXls(products: Product[]): string {
  const headerCells = PRODUCT_EXCEL_HEADERS.map(
    (h) => `<Cell><Data ss:Type="String">${xmlEsc(h)}</Data></Cell>`
  ).join('');
  const bodyRows = products
    .map((p) => {
      const cells = productToRow(p)
        .map((v, i) => {
          // Price / Stock columns (2, 7) are numeric where possible.
          const numeric = (i === 2 || i === 7) && v !== '' && !isNaN(Number(v));
          const type = numeric ? 'Number' : 'String';
          return `<Cell><Data ss:Type="${type}">${xmlEsc(v)}</Data></Cell>`;
        })
        .join('');
      return `<Row>${cells}</Row>`;
    })
    .join('');
  return (
    '<?xml version="1.0"?>\n' +
    '<?mso-application progid="Excel.Sheet"?>\n' +
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ' +
    'xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n' +
    '<Worksheet ss:Name="Product Details"><Table>' +
    `<Row>${headerCells}</Row>${bodyRows}` +
    '</Table></Worksheet></Workbook>'
  );
}

// Import template — the header row plus example rows showing the expected values
// (YES/NO flags, numeric Price / Stock / Tax). The 5th column is the optional
// Sub Category. Fill it in, keep the header row, and import it back.
export function buildProductTemplateCsv(): string {
  const samples: string[][] = [
    ['Sugar 1kg', '', '48', 'Grocery', 'Sugar & Salt', 'YES', 'NO', '100', '0'],
    ['Chicken Biriyani', '', '180', 'Food', 'Biriyani', 'NO', 'NO', '0', '5'],
    ['Tomato', '', '32', 'Vegetables', 'Fresh', 'YES', 'YES', '25.5', '0'],
  ];
  const lines = [PRODUCT_EXCEL_HEADERS.join(',')];
  samples.forEach((r) => lines.push(r.map(csvCell).join(',')));
  return lines.join('\r\n');
}

export function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Import ----

export interface ImportRow {
  name: string;
  regionalName: string;
  price: number;
  category: string;
  subCategory: string;
  stockable: boolean;
  weighable: boolean;
  stock: number;
  taxRatePercent?: number;
}

// Parse one CSV line honouring quoted fields.
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

function rowsFromCsv(text: string): string[][] {
  return text
    .split(/\r?\n/)
    .filter((l) => l.trim() !== '')
    .map(parseCsvLine);
}

// Pull <Row>/<Cell>/<Data> out of Excel 2003 SpreadsheetML.
function rowsFromSpreadsheetMl(text: string): string[][] {
  const rows: string[][] = [];
  const rowMatches = text.match(/<Row[\s\S]*?<\/Row>/gi) || [];
  rowMatches.forEach((r) => {
    const cells: string[] = [];
    const cellMatches = r.match(/<Cell[\s\S]*?<\/Cell>|<Cell[^>]*\/>/gi) || [];
    cellMatches.forEach((c) => {
      const data = c.match(/<Data[^>]*>([\s\S]*?)<\/Data>/i);
      const raw = data ? data[1] : '';
      cells.push(
        raw
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
      );
    });
    rows.push(cells);
  });
  return rows;
}

const truthy = (s: string) => /^(y|yes|true|1)$/i.test(s.trim());
const num = (s: string) => {
  const m = s.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
};

// Parse an uploaded file (CSV or SpreadsheetML) into product rows. Columns are
// located by their header labels when a header row is present (first cell
// "Product Name"), so both the current layout and legacy 8-column files (no
// "Product Sub Category" column) import correctly. Without a recognisable header
// the legacy fixed-order 8-column layout is assumed. Rows with no name are
// dropped, matching ExcelParser's empty-row guard.
export function parseProductImport(text: string): ImportRow[] {
  const isXml = /^\s*<\?xml|<Workbook/i.test(text);
  const raw = isXml ? rowsFromSpreadsheetMl(text) : rowsFromCsv(text);
  if (raw.length === 0) return [];

  const header = raw[0].map((h) => h.trim().toLowerCase());
  const hasHeader = header[0] === 'product name';
  const find = (label: string, fallback: number) => {
    const i = header.indexOf(label);
    return i >= 0 ? i : fallback;
  };
  // Header-driven column map when a header exists, else the legacy fixed order.
  const col = hasHeader
    ? {
        name: find('product name', 0),
        regionalName: find('regional name', 1),
        price: find('product price', 2),
        category: find('product category', 3),
        subCategory: find('product sub category', -1),
        stockable: find('stockable', 4),
        weighable: find('weightable', 5),
        stock: find('stock', 6),
        tax: find('tax', 7),
      }
    : { name: 0, regionalName: 1, price: 2, category: 3, subCategory: -1, stockable: 4, weighable: 5, stock: 6, tax: 7 };

  const out: ImportRow[] = [];
  raw.forEach((cells, idx) => {
    const c = (i: number) => (i >= 0 ? (cells[i] ?? '').trim() : '');
    if (idx === 0 && hasHeader) return; // skip header row
    if (!c(col.name)) return;
    out.push({
      name: c(col.name),
      regionalName: c(col.regionalName),
      price: num(c(col.price)),
      category: c(col.category),
      subCategory: c(col.subCategory),
      stockable: truthy(c(col.stockable)),
      weighable: truthy(c(col.weighable)),
      stock: num(c(col.stock)),
      taxRatePercent: c(col.tax) ? num(c(col.tax)) : undefined,
    });
  });
  return out;
}
